import { Text, View } from "@react-pdf/renderer";
import dayjs from "dayjs";
import { payments } from "lobbie";
import { recurrenceFreqToUnit } from "src/types/global_enums";
import styles, { pdfColors } from "../base/styles";

interface IProps {
    invoiceDetails: payments.print.IInvoicePrintData;
    applyTax: boolean;
    receiptDetails?: payments.print.IPaymentRequest;
}

const InvoiceTotals: React.FC<IProps> = ({ invoiceDetails, applyTax, receiptDetails }) => {
    return (
        <View id="invoice-totals" style={[styles.pt16, { alignItems: "flex-end" }]} fixed>
            <View id="invoice-totals-col" style={[styles.col, styles.pb8, { width: "250px" }]}>
                <View id="invoice-due-now" style={[styles.col, styles.w100]}>
                    {applyTax &&
                        !!invoiceDetails.taxSettings?.salesTax &&
                        !!invoiceDetails.subTotalAmountCents && (
                            <>
                                <View
                                    style={[
                                        styles.row,
                                        styles.w100,
                                        styles.pb6,
                                        styles.pt6,
                                        styles.justBetween,
                                        {
                                            borderBottom: "0.5px",
                                            borderBottomColor: pdfColors.Gray20,
                                        },
                                    ]}
                                >
                                    <Text>Subtotal:</Text>
                                    <View style={[styles.col, { alignItems: "flex-end" }]}>
                                        <Text>
                                            {`$ ${(
                                                invoiceDetails.subTotalAmountCents / 100
                                            ).toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                            })}`}
                                        </Text>
                                    </View>
                                </View>
                                <View
                                    style={[
                                        styles.row,
                                        styles.w100,
                                        styles.pb6,
                                        styles.pt6,
                                        styles.justBetween,
                                        {
                                            borderBottom: "0.5px",
                                            borderBottomColor: pdfColors.Gray20,
                                        },
                                    ]}
                                >
                                    <Text>{`Sales Tax (${invoiceDetails.taxSettings.salesTax}%) - ${invoiceDetails.taxSettings.provinceName}:`}</Text>
                                    <View style={[styles.col, { alignItems: "flex-end" }]}>
                                        <Text>{`$ ${(
                                            (invoiceDetails.totalAmountCents -
                                                invoiceDetails.subTotalAmountCents) /
                                            100
                                        ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                        })}`}</Text>
                                    </View>
                                </View>
                            </>
                        )}
                    {receiptDetails && (
                        <View
                            style={[
                                styles.row,
                                styles.w100,
                                styles.pb6,
                                styles.pt6,
                                styles.justBetween,
                                {
                                    borderBottom: "0.5px",
                                    borderBottomColor: pdfColors.Gray20,
                                },
                            ]}
                        >
                            <Text>Total Charges:</Text>
                            <View style={[styles.col, { alignItems: "flex-end" }]}>
                                <Text>
                                    {`$ ${(receiptDetails.amountCents / 100).toLocaleString(
                                        undefined,
                                        {
                                            minimumFractionDigits: 2,
                                        },
                                    )}`}
                                </Text>
                            </View>
                        </View>
                    )}
                    <View
                        style={[
                            styles.row,
                            styles.w100,
                            styles.pb12,
                            styles.pt12,
                            styles.justBetween,
                            { borderBottom: "0.5px", borderBottomColor: pdfColors.Gray20 },
                            { fontSize: "10px" },
                        ]}
                    >
                        <Text
                            style={[styles.textBold, { fontSize: "10px" }]}
                        >{`${receiptDetails ? "Amount Paid:" : "Due Now:"}`}</Text>
                        <View style={[styles.col, { alignItems: "flex-end" }]}>
                            <Text style={[styles.textBold]}>
                                {`$ ${(receiptDetails
                                    ? receiptDetails.amountCents / 100
                                    : invoiceDetails.totalAmountCents / 100
                                ).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                })}`}
                            </Text>
                        </View>
                    </View>
                </View>
                {!receiptDetails &&
                    invoiceDetails.recurringPayments &&
                    invoiceDetails.recurringPayments.length !== 0 && (
                        <View
                            style={[
                                styles.col,
                                styles.w100,
                                styles.pt6,
                                {
                                    gap: "8px",
                                },
                            ]}
                        >
                            <Text>Subscription Details:</Text>
                            {invoiceDetails.recurringPayments?.map((recurringPayment) => (
                                <SubscriptionDetails
                                    key={recurringPayment.id}
                                    recurringPayment={recurringPayment}
                                    timezone={invoiceDetails.timezone}
                                />
                            ))}
                        </View>
                    )}
            </View>
        </View>
    );
};

interface ISubscriptionDetailsProps {
    recurringPayment: payments.print.IRecurringPaymentDetails;
    timezone: string;
}

const SubscriptionDetails: React.FC<ISubscriptionDetailsProps> = ({
    recurringPayment,
    timezone,
}) => {
    let subscriptionType = "";
    if (!recurringPayment.numberOfOccurrences && recurringPayment.numberOfOccurrences === 0) {
        subscriptionType = "Ongoing";
    } else {
        subscriptionType = "Finite";
    }
    const startDate = dayjs.tz(recurringPayment.startDateISO, timezone).format("MM / DD / YYYY");

    const formattedAmount = (recurringPayment.recurringAmountCents / 100).toLocaleString(
        undefined,
        {
            minimumFractionDigits: 2,
        },
    );
    const frequencyUnit = recurringPayment.recurrenceFrequencyUnit
        ? recurrenceFreqToUnit.get(recurringPayment.recurrenceFrequencyUnit)
        : "";

    return (
        <View
            style={[
                styles.col,
                styles.pb6,
                { gap: "4px", borderBottom: "0.5px", borderBottomColor: pdfColors.Gray20 },
            ]}
        >
            <View style={[styles.row, styles.w100, { gap: "10px" }]}>
                <View style={[{ width: "62px" }]}>
                    <Text style={[styles.textGray65]}>Subscription ID:</Text>
                </View>
                <Text>{recurringPayment.subscriptionId}</Text>
            </View>

            <View style={[styles.row, styles.w100, { gap: "10px" }]}>
                <View style={[{ width: "62px" }]}>
                    <Text style={[styles.textGray65]}>Type:</Text>
                </View>
                <Text>{subscriptionType}</Text>
            </View>
            <View style={[styles.row, styles.w100, { gap: "10px" }]}>
                <View style={[{ width: "62px" }]}>
                    <Text style={[styles.textGray65]}>Start Date:</Text>
                </View>
                <Text>{startDate}</Text>
            </View>
            {recurringPayment.careServiceName && (
                <View style={[styles.row, styles.w100, { gap: "10px" }]}>
                    <View style={[{ width: "62px" }]}>
                        <Text style={[styles.textGray65]}>Service:</Text>
                    </View>
                    <Text>{recurringPayment.careServiceName}</Text>
                </View>
            )}
            <View style={[styles.row, styles.w100, { gap: "10px" }]}>
                <View style={[{ width: "62px" }]}>
                    <Text style={[styles.textGray65]}>Charge:</Text>
                </View>
                <Text>{`$${formattedAmount}/${frequencyUnit}`}</Text>
            </View>
        </View>
    );
};

export default InvoiceTotals;
